import * as React from 'react';

import { useIsMobile } from './useIsMobile';
import { lockScroll, unlockScroll } from 'src/utils/bodyScroll';

/**
 * Shared mobile drawer behaviour for the search-filter rail that lives
 * on the Assets, Collections, and Shares list pages.
 *
 * The pages own the "filters visible" state (each uses a slightly
 * different storage — local React state, a Jotai atom, or a dashboard
 * hook state) so this hook is intentionally state-light: it takes the
 * existing boolean + setter and layers on top of them:
 *
 *  - A `lockScroll` / `unlockScroll` pair while the drawer is open on
 *    mobile, so the page underneath doesn't rubber-band on iOS.
 *  - An Escape-to-close keyboard shortcut.
 *  - A `closeIfMobile()` helper for the scrim overlay click handler
 *    and for "Apply filters" buttons that should dismiss after submit.
 *
 * Desktop behaviour is unchanged: the filter rail keeps inline-flow
 * layout and this hook is a no-op when `isMobile === false`.
 */
export interface UseFiltersDrawerOptions {
  visible: boolean;
  setVisible: (next: boolean) => void;
}

export interface UseFiltersDrawerResult {
  isMobile: boolean;
  drawerOpen: boolean;
  closeIfMobile: () => void;
}

export function useFiltersDrawer({
  visible,
  setVisible,
}: UseFiltersDrawerOptions): UseFiltersDrawerResult {
  const isMobile = useIsMobile();
  const drawerOpen = isMobile && visible;

  React.useEffect(() => {
    if (!drawerOpen) return;
    lockScroll();
    return () => unlockScroll();
  }, [drawerOpen]);

  React.useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setVisible(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen, setVisible]);

  const closeIfMobile = React.useCallback(() => {
    if (isMobile) setVisible(false);
  }, [isMobile, setVisible]);

  return { isMobile, drawerOpen, closeIfMobile };
}

export default useFiltersDrawer;
